/** Pan/zoom math. screen = (world - scroll) * zoom. */

import { MAX_ZOOM, MIN_ZOOM, type BoardView, type SlateElement } from './types';
import { unionRects, worldBounds, type Rect, type Vec } from '../geometry/transform';

export const DEFAULT_VIEW: BoardView = { scrollX: 0, scrollY: 0, zoom: 1 };

export function screenToWorld(view: BoardView, screen: Vec): Vec {
  return { x: screen.x / view.zoom + view.scrollX, y: screen.y / view.zoom + view.scrollY };
}

export function worldToScreen(view: BoardView, world: Vec): Vec {
  return { x: (world.x - view.scrollX) * view.zoom, y: (world.y - view.scrollY) * view.zoom };
}

export function clampZoom(zoom: number): number {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, zoom));
}

/** Zoom while keeping the world point under `anchorScreen` stationary. */
export function zoomAt(view: BoardView, nextZoom: number, anchorScreen: Vec): BoardView {
  const zoom = clampZoom(nextZoom);
  const before = screenToWorld(view, anchorScreen);
  const after = { x: anchorScreen.x / zoom + view.scrollX, y: anchorScreen.y / zoom + view.scrollY };
  return { zoom, scrollX: view.scrollX + (before.x - after.x), scrollY: view.scrollY + (before.y - after.y) };
}

export function sceneBounds(elements: readonly SlateElement[]): Rect | null {
  const rects = elements.filter((el) => !el.isDeleted).map(worldBounds);
  return unionRects(rects);
}

export function zoomToFit(
  elements: readonly SlateElement[],
  viewport: { width: number; height: number },
  padding = 64,
): BoardView {
  const bounds = sceneBounds(elements);
  // Nothing to fit: leave the view where a new board starts.
  if (!bounds) return DEFAULT_VIEW;
  const usableW = Math.max(1, viewport.width - padding * 2);
  const usableH = Math.max(1, viewport.height - padding * 2);
  const zoom = clampZoom(
    Math.min(usableW / Math.max(bounds.width, 1), usableH / Math.max(bounds.height, 1), 1),
  );
  const centerX = bounds.x + bounds.width / 2;
  const centerY = bounds.y + bounds.height / 2;
  return {
    zoom,
    scrollX: centerX - viewport.width / (2 * zoom),
    scrollY: centerY - viewport.height / (2 * zoom),
  };
}

/** Centre the view on a rectangle without changing zoom. */
export function centerOn(view: BoardView, rect: Rect, viewport: { width: number; height: number }): BoardView {
  const centerX = rect.x + rect.width / 2;
  const centerY = rect.y + rect.height / 2;
  return {
    zoom: view.zoom,
    scrollX: centerX - viewport.width / (2 * view.zoom),
    scrollY: centerY - viewport.height / (2 * view.zoom),
  };
}
